<template>
  <div class="music-library">
    <n-card>
      <template #header>
        <div class="card-header">
          <span>Music Library (ReelsGen)</span>
          <n-space>
            <n-select
              v-model:value="filters.category"
              :options="categoryOptions"
              placeholder="Category"
              clearable
              style="width: 150px"
            />
            <n-checkbox v-model:checked="filters.showInactive">
              Show Inactive
            </n-checkbox>
            <n-button type="primary" @click="openAddModal">
              Add Track
            </n-button>
          </n-space>
        </div>
      </template>

      <n-data-table
        :columns="columns"
        :data="filteredTracks"
        :loading="loading"
        :row-key="(row) => row.id"
      />

      <!-- Audio Player Bar -->
      <div v-if="currentlyPlaying" class="player-bar">
        <div class="player-info">
          <span class="track-name">{{ currentTrackInfo?.name || 'Unknown' }}</span>
          <span class="track-artist">{{ currentTrackInfo?.artist || '' }}</span>
        </div>
        <div class="player-controls">
          <n-button size="small" @click="togglePlay({ id: currentlyPlaying, file_url: audioPlayer?.src })">
            {{ isPlaying ? 'Pause' : 'Play' }}
          </n-button>
          <n-button size="small" @click="stopPlayback">Stop</n-button>
        </div>
        <div class="player-progress">
          <span class="time">{{ formatTime(currentTime) }}</span>
          <n-slider
            :value="currentTime"
            :max="duration || 100"
            :step="0.1"
            :tooltip="false"
            style="flex: 1; margin: 0 12px"
            @update:value="handleSeek"
          />
          <span class="time">{{ formatTime(duration) }}</span>
        </div>
      </div>
    </n-card>

    <!-- Add/Edit Track Modal -->
    <n-modal v-model:show="showModal" preset="dialog" :title="editingTrack ? 'Edit Track' : 'Add Track'">
      <n-tabs v-if="!editingTrack" v-model:value="addMode">
        <n-tab-pane name="manual" tab="Manual Entry">
          <n-form :model="form" label-placement="left" label-width="120">
            <n-form-item label="Name" required>
              <n-input v-model:value="form.name" placeholder="Track name" />
            </n-form-item>
            <n-form-item label="Artist">
              <n-input v-model:value="form.artist" placeholder="Artist name" />
            </n-form-item>
            <n-form-item label="Duration (sec)" required>
              <n-input-number v-model:value="form.duration_seconds" :min="1" />
            </n-form-item>
            <n-form-item label="Category" required>
              <n-select v-model:value="form.category" :options="categoryOptions" />
            </n-form-item>
            <n-form-item label="Mood">
              <n-select v-model:value="form.mood" :options="moodOptions" clearable />
            </n-form-item>
            <n-form-item label="Tempo">
              <n-select v-model:value="form.tempo" :options="tempoOptions" clearable />
            </n-form-item>
            <n-form-item label="File URL" required>
              <n-input v-model:value="form.file_url" placeholder="https://..." />
            </n-form-item>
            <n-form-item label="License">
              <n-select v-model:value="form.license" :options="licenseOptions" />
            </n-form-item>
            <n-form-item label="Source">
              <n-select v-model:value="form.source" :options="sourceOptions" clearable />
            </n-form-item>
          </n-form>
        </n-tab-pane>

        <n-tab-pane name="import" tab="Import from URL">
          <n-alert type="info" style="margin-bottom: 16px">
            Download music from FreePD, Pixabay, etc. and upload to R2 storage.
          </n-alert>
          <n-form :model="importForm" label-placement="left" label-width="120">
            <n-form-item label="Source URL" required>
              <n-input v-model:value="importForm.url" placeholder="https://freepd.com/..." />
            </n-form-item>
            <n-form-item label="Name" required>
              <n-input v-model:value="importForm.name" placeholder="Track name" />
            </n-form-item>
            <n-form-item label="Artist">
              <n-input v-model:value="importForm.artist" placeholder="Artist name" />
            </n-form-item>
            <n-form-item label="Category" required>
              <n-select v-model:value="importForm.category" :options="categoryOptions" />
            </n-form-item>
            <n-form-item label="Mood">
              <n-select v-model:value="importForm.mood" :options="moodOptions" clearable />
            </n-form-item>
            <n-form-item label="Tempo">
              <n-select v-model:value="importForm.tempo" :options="tempoOptions" clearable />
            </n-form-item>
            <n-form-item label="License">
              <n-select v-model:value="importForm.license" :options="licenseOptions" />
            </n-form-item>
            <n-form-item label="Source">
              <n-select v-model:value="importForm.source" :options="sourceOptions" />
            </n-form-item>
          </n-form>
        </n-tab-pane>

        <n-tab-pane name="upload" tab="Upload File">
          <n-alert type="info" style="margin-bottom: 16px">
            Upload an MP3 file directly. After upload, fill in the track details.
          </n-alert>
          <n-upload
            :custom-request="handleUpload"
            :max="1"
            accept=".mp3,.wav,.m4a"
            @finish="handleUploadFinish"
          >
            <n-button>Select File</n-button>
          </n-upload>
          <div v-if="uploadedUrl" style="margin-top: 16px">
            <n-tag type="success">File uploaded successfully</n-tag>
            <n-form :model="form" label-placement="left" label-width="120" style="margin-top: 16px">
              <n-form-item label="Name" required>
                <n-input v-model:value="form.name" placeholder="Track name" />
              </n-form-item>
              <n-form-item label="Artist">
                <n-input v-model:value="form.artist" placeholder="Artist name" />
              </n-form-item>
              <n-form-item label="Duration (sec)" required>
                <n-input-number v-model:value="form.duration_seconds" :min="1" />
              </n-form-item>
              <n-form-item label="Category" required>
                <n-select v-model:value="form.category" :options="categoryOptions" />
              </n-form-item>
              <n-form-item label="Mood">
                <n-select v-model:value="form.mood" :options="moodOptions" clearable />
              </n-form-item>
              <n-form-item label="Tempo">
                <n-select v-model:value="form.tempo" :options="tempoOptions" clearable />
              </n-form-item>
              <n-form-item label="License">
                <n-select v-model:value="form.license" :options="licenseOptions" />
              </n-form-item>
              <n-form-item label="Source">
                <n-select v-model:value="form.source" :options="sourceOptions" clearable />
              </n-form-item>
            </n-form>
          </div>
        </n-tab-pane>
      </n-tabs>

      <!-- Edit Mode (no tabs) -->
      <n-form v-else :model="form" label-placement="left" label-width="120">
        <n-form-item label="Name">
          <n-input v-model:value="form.name" placeholder="Track name" />
        </n-form-item>
        <n-form-item label="Artist">
          <n-input v-model:value="form.artist" placeholder="Artist name" />
        </n-form-item>
        <n-form-item label="Category">
          <n-select v-model:value="form.category" :options="categoryOptions" />
        </n-form-item>
        <n-form-item label="Mood">
          <n-select v-model:value="form.mood" :options="moodOptions" clearable />
        </n-form-item>
        <n-form-item label="Tempo">
          <n-select v-model:value="form.tempo" :options="tempoOptions" clearable />
        </n-form-item>
        <n-form-item label="Active">
          <n-switch v-model:value="form.is_active" />
        </n-form-item>
      </n-form>

      <template #action>
        <n-button @click="closeModal">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="handleSave">
          {{ editingTrack ? 'Update' : 'Save' }}
        </n-button>
      </template>
    </n-modal>

    <!-- Audio Preview Player -->
    <audio ref="audioPlayer" style="display: none" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue'
import {
  NCard,
  NDataTable,
  NSelect,
  NCheckbox,
  NSpace,
  NTag,
  NButton,
  NModal,
  NForm,
  NFormItem,
  NInput,
  NInputNumber,
  NTabs,
  NTabPane,
  NUpload,
  NAlert,
  NSwitch,
  NSlider,
} from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAdmin } from '../composables/useAdmin'

const toast = useToast()
const {
  listMusicTracks,
  createMusicTrack,
  updateMusicTrack,
  deleteMusicTrack,
  importMusicFromURL,
  uploadMusicFile,
  loading,
} = useAdmin()

const tracks = ref([])
const showModal = ref(false)
const saving = ref(false)
const editingTrack = ref(null)
const addMode = ref('manual')
const uploadedUrl = ref(null)
const audioPlayer = ref(null)
const currentlyPlaying = ref(null)
const currentTrackInfo = ref(null)
const currentTime = ref(0)
const duration = ref(0)
const isPlaying = ref(false)

const filters = ref({
  category: null,
  showInactive: true,
})

const form = ref({
  name: '',
  artist: '',
  duration_seconds: 120,
  category: 'upbeat',
  mood: null,
  tempo: null,
  file_url: '',
  license: 'CC0',
  source: null,
  is_active: true,
})

const importForm = ref({
  url: '',
  name: '',
  artist: '',
  category: 'upbeat',
  mood: null,
  tempo: null,
  license: 'CC0',
  source: 'freepd',
})

const categoryOptions = [
  { label: 'Upbeat', value: 'upbeat' },
  { label: 'Calm', value: 'calm' },
  { label: 'Dramatic', value: 'dramatic' },
  { label: 'Festive', value: 'festive' },
  { label: 'Traditional', value: 'traditional' },
  { label: 'Trendy', value: 'trendy' },
]

const moodOptions = [
  { label: 'Happy', value: 'happy' },
  { label: 'Energetic', value: 'energetic' },
  { label: 'Peaceful', value: 'peaceful' },
  { label: 'Intense', value: 'intense' },
  { label: 'Romantic', value: 'romantic' },
]

const tempoOptions = [
  { label: 'Slow', value: 'slow' },
  { label: 'Medium', value: 'medium' },
  { label: 'Fast', value: 'fast' },
]

const licenseOptions = [
  { label: 'CC0 (Public Domain)', value: 'CC0' },
  { label: 'CC-BY (Attribution)', value: 'CC-BY' },
]

const sourceOptions = [
  { label: 'FreePD', value: 'freepd' },
  { label: 'Pixabay', value: 'pixabay' },
  { label: 'Free Music Archive', value: 'fma' },
  { label: 'Other', value: 'other' },
]

const filteredTracks = computed(() => {
  let result = tracks.value
  if (filters.value.category) {
    result = result.filter((t) => t.category === filters.value.category)
  }
  if (!filters.value.showInactive) {
    result = result.filter((t) => t.is_active)
  }
  return result
})

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const columns = [
  { title: 'ID', key: 'id', width: 60 },
  { title: 'Name', key: 'name' },
  { title: 'Artist', key: 'artist' },
  {
    title: 'Duration',
    key: 'duration_seconds',
    width: 80,
    render: (row) => formatDuration(row.duration_seconds),
  },
  {
    title: 'Category',
    key: 'category',
    width: 100,
    render: (row) => h(NTag, { type: 'info', size: 'small' }, () => row.category),
  },
  {
    title: 'Mood',
    key: 'mood',
    width: 100,
    render: (row) => (row.mood ? h(NTag, { size: 'small' }, () => row.mood) : '-'),
  },
  {
    title: 'Status',
    key: 'is_active',
    width: 80,
    render: (row) =>
      h(
        NTag,
        { type: row.is_active ? 'success' : 'error', size: 'small' },
        () => (row.is_active ? 'Active' : 'Inactive')
      ),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 200,
    render: (row) => {
      return h(NSpace, { size: 'small' }, () => [
        h(
          NButton,
          {
            size: 'small',
            type: currentlyPlaying.value === row.id ? 'warning' : 'default',
            onClick: () => togglePlay(row),
          },
          () => (currentlyPlaying.value === row.id ? (isPlaying.value ? 'Pause' : 'Resume') : 'Play')
        ),
        h(
          NButton,
          {
            size: 'small',
            onClick: () => openEditModal(row),
          },
          () => 'Edit'
        ),
        h(
          NButton,
          {
            size: 'small',
            type: row.is_active ? 'error' : 'success',
            onClick: () => handleToggleActive(row),
          },
          () => (row.is_active ? 'Deactivate' : 'Activate')
        ),
      ])
    },
  },
]

const togglePlay = (track) => {
  if (currentlyPlaying.value === track.id) {
    if (isPlaying.value) {
      audioPlayer.value.pause()
      isPlaying.value = false
    } else {
      audioPlayer.value.play()
      isPlaying.value = true
    }
  } else {
    audioPlayer.value.src = track.file_url
    audioPlayer.value.play()
    currentlyPlaying.value = track.id
    currentTrackInfo.value = { name: track.name, artist: track.artist }
    isPlaying.value = true
  }
}

const stopPlayback = () => {
  audioPlayer.value.pause()
  audioPlayer.value.currentTime = 0
  currentlyPlaying.value = null
  currentTrackInfo.value = null
  currentTime.value = 0
  duration.value = 0
  isPlaying.value = false
}

const handleSeek = (value) => {
  if (audioPlayer.value) {
    audioPlayer.value.currentTime = value
    currentTime.value = value
  }
}

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const openAddModal = () => {
  editingTrack.value = null
  addMode.value = 'manual'
  uploadedUrl.value = null
  form.value = {
    name: '',
    artist: '',
    duration_seconds: 120,
    category: 'upbeat',
    mood: null,
    tempo: null,
    file_url: '',
    license: 'CC0',
    source: null,
    is_active: true,
  }
  importForm.value = {
    url: '',
    name: '',
    artist: '',
    category: 'upbeat',
    mood: null,
    tempo: null,
    license: 'CC0',
    source: 'freepd',
  }
  showModal.value = true
}

const openEditModal = (track) => {
  editingTrack.value = track
  form.value = {
    name: track.name,
    artist: track.artist || '',
    category: track.category,
    mood: track.mood || null,
    tempo: track.tempo || null,
    is_active: track.is_active,
  }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingTrack.value = null
  uploadedUrl.value = null
}

const handleUpload = async ({ file, onFinish, onError }) => {
  try {
    const result = await uploadMusicFile(file.file)
    uploadedUrl.value = result.file_url
    form.value.file_url = result.file_url
    form.value.name = file.name.replace(/\.[^/.]+$/, '')
    onFinish()
    toast.success('File uploaded successfully')
  } catch (error) {
    onError()
    toast.error(error.message)
  }
}

const handleUploadFinish = () => {
  // Upload complete
}

const handleSave = async () => {
  saving.value = true
  try {
    if (editingTrack.value) {
      // Update existing track
      await updateMusicTrack(editingTrack.value.id, {
        name: form.value.name || undefined,
        artist: form.value.artist || undefined,
        category: form.value.category || undefined,
        mood: form.value.mood || undefined,
        tempo: form.value.tempo || undefined,
        is_active: form.value.is_active,
      })
      toast.success('Track updated successfully')
    } else if (addMode.value === 'import') {
      // Import from URL
      if (!importForm.value.url || !importForm.value.name || !importForm.value.category) {
        toast.error('URL, name, and category are required')
        return
      }
      await importMusicFromURL(importForm.value)
      toast.success('Track imported successfully')
    } else {
      // Manual entry or after upload
      const fileUrl = uploadedUrl.value || form.value.file_url
      if (!form.value.name || !fileUrl || !form.value.category) {
        toast.error('Name, file URL, and category are required')
        return
      }
      await createMusicTrack({
        name: form.value.name,
        artist: form.value.artist || undefined,
        duration_seconds: form.value.duration_seconds,
        category: form.value.category,
        mood: form.value.mood || undefined,
        tempo: form.value.tempo || undefined,
        file_url: fileUrl,
        license: form.value.license,
        source: form.value.source || undefined,
      })
      toast.success('Track created successfully')
    }
    closeModal()
    loadTracks()
  } catch (error) {
    toast.error(error.message)
  } finally {
    saving.value = false
  }
}

const handleToggleActive = async (track) => {
  try {
    await updateMusicTrack(track.id, { is_active: !track.is_active })
    toast.success(track.is_active ? 'Track deactivated' : 'Track activated')
    loadTracks()
  } catch (error) {
    toast.error(error.message)
  }
}

const loadTracks = async () => {
  try {
    const response = await listMusicTracks()
    tracks.value = response.tracks || []
  } catch (error) {
    console.error('Failed to load tracks:', error)
    toast.error('Failed to load music tracks')
  }
}

onMounted(() => {
  loadTracks()

  // Wire up audio player events
  if (audioPlayer.value) {
    audioPlayer.value.addEventListener('timeupdate', () => {
      currentTime.value = audioPlayer.value.currentTime
    })

    audioPlayer.value.addEventListener('loadedmetadata', () => {
      duration.value = audioPlayer.value.duration
    })

    audioPlayer.value.addEventListener('ended', () => {
      currentlyPlaying.value = null
      currentTrackInfo.value = null
      currentTime.value = 0
      duration.value = 0
      isPlaying.value = false
    })
  }
})
</script>

<style scoped>
.music-library {
  max-width: 1400px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.player-bar {
  margin-top: 16px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.player-info {
  min-width: 150px;
}

.track-name {
  font-weight: 600;
  display: block;
}

.track-artist {
  font-size: 12px;
  color: #666;
}

.player-controls {
  display: flex;
  gap: 8px;
}

.player-progress {
  flex: 1;
  display: flex;
  align-items: center;
}

.player-progress .time {
  font-size: 12px;
  color: #666;
  min-width: 40px;
  text-align: center;
}
</style>
